'use client';

import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { parseAsString, useQueryState } from 'nuqs';
import { toast } from 'sonner';

import { PageMain } from '@/app/(app)/page-main';
import { FloatingActionButtons } from '@/components/molecules/floating-action-buttons';
import { ServiceTypeTemplate, useServiceTypeTemplatesCtx } from '@/components/providers/service-templates-provider';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs } from '@/components/ui/tabs';
import { useEditableStateUpdates } from '@/hooks/use-editable-state';
import { useMetadata } from '@/hooks/use-metadata';
import { formatCustomerName } from '@/lib/format';

type ApiCustomer = ApiSchemas['CustomerReadComplete'];
type ApiCustomerServiceType = ApiCustomer['service_types'][number];

export type CustomerServiceType = ApiCustomerServiceType & {
  template: ServiceTypeTemplate | undefined;
};

// Create the update type, we can't use the api schema directly
// because all of the fields are nullable which isn't the case for the read schema
type ApiCustomerUpdate = Pick<ApiCustomer, keyof ApiSchemas['CustomerUpdate']>;

export type CustomerComplete = Omit<ApiCustomer, 'service_types'> & {
  service_types: CustomerServiceType[];
  primary_location: ApiSchemas['LocationRead'] | undefined;
  primary_phone: string | undefined;
  primary_email: string | undefined;
};

interface Context {
  customer: CustomerComplete;
  serviceTypesById: Map<string, CustomerServiceType>;
  updateCustomer: (values: Partial<ApiCustomerUpdate>) => void;
  tab: string;
  setTab: (tab: string) => void;
  refetch: () => void;
}

const CustomerContext = createContext<Context | null>(null);

export function useCustomerCtx() {
  const ctx = useContext(CustomerContext);
  if (!ctx) {
    throw new Error('useCustomerCtx must be used within a CustomerProvider');
  }
  return ctx;
}

export function CustomerProvider({ id, children }: { id: string; children: React.ReactNode }) {
  const { customer, refetch } = useApiCustomer(id);

  if (!customer) {
    return (
      <PageMain title='Customers'>
        <Skeleton className='h-28 w-full' />
        <Skeleton className='h-9 w-full' />
        <Skeleton className='h-96 w-full' />
      </PageMain>
    );
  }

  return (
    <InternalProvider customer={customer} refetch={refetch}>
      {children}
    </InternalProvider>
  );
}

interface InternalProviderProps {
  customer: CustomerComplete;
  refetch: () => void;
  children: React.ReactNode;
}

function InternalProvider({ customer, refetch, children }: InternalProviderProps) {
  const { api } = useApiV1Ctx();
  const [tab, setTab] = useQueryState('tab', parseAsString.withDefault('overview'));

  const serviceTypesById = useMemo(() => {
    const map = new Map<string, CustomerServiceType>();
    customer.service_types.forEach((serviceType) => {
      map.set(serviceType.id, serviceType);
    });
    return map;
  }, [customer]);

  const state = useEditableStateUpdates(customer, (v) => {
    return {
      ...v,
      name: formatCustomerName(v),
    };
  });

  useMetadata({ title: state.value.name });

  useEffect(() => {
    state.reset(customer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const mutation = api.customers.updateCustomer.useMutation(
    {
      path: { customer_id: customer.id },
    },
    {
      onSuccess: () => {
        toast.success('Customer updated');
        refetch();
      },
    }
  );

  const onSave = useCallback(() => {
    mutation.mutate(state.updates);
  }, [state, mutation]);

  return (
    <CustomerContext.Provider
      value={{ customer: state.value, updateCustomer: state.setUpdates, serviceTypesById, tab, setTab, refetch }}
    >
      <Tabs defaultValue='overview' value={tab} onValueChange={(value) => setTab(value as typeof tab)}>
        {children}
      </Tabs>
      {state.isDirty && (
        <FloatingActionButtons onSave={onSave} onDiscard={() => state.reset(customer)} disabled={mutation.isPending} />
      )}
    </CustomerContext.Provider>
  );
}

function useApiCustomer(id: string) {
  const { api } = useApiV1Ctx();
  const { serviceTypeTemplatesById } = useServiceTypeTemplatesCtx();
  const { data, refetch } = api.customers.getCustomer.useQuery(
    {
      path: { customer_id: id },
    },
    { refetchOnWindowFocus: false }
  );
  const customer: CustomerComplete | null = useMemo(() => {
    if (!data) return null;
    const serviceTypes = data.service_types.map((serviceType) => {
      const template = serviceTypeTemplatesById.get(serviceType.template_id ?? '');
      return {
        ...serviceType,
        template,
      } as CustomerServiceType;
    });
    return {
      ...data,
      service_types: serviceTypes,
      primary_location: data.locations.find((location) => location.primary) ?? data.locations[0],
      primary_phone: data.contacts.find((contact) => contact.type === 'PHONE' && contact.primary)?.value,
      primary_email: data.contacts.find((contact) => contact.type === 'EMAIL' && contact.primary)?.value,
    };
  }, [data, serviceTypeTemplatesById]);

  return { customer, refetch };
}
